import type { BattleState } from '@somnhold/sim';
import { seasonNow } from '../season.ts';
import { audio } from './sfx.ts';

/**
 * Music (SPEC §15): three moods — calm (lute, harp, low strings, choir), Marea (war drums and a
 * string ostinato) and boss (faster, darker, choir stabs), with its own for the Unsleeping; and two of their own for some fights: deep (crypts,
 * cellars, the Sima and the saga's halls) and night (a side saga's fight at night). If `public/audio/{mood}.mp3`
 * exist they are streamed; otherwise a small generative ensemble plays in Web Audio. During a season
 * (Halloween…) its own takes, `{mood}-{season}.mp3`, play instead when they exist.
 */
export type Mood = 'calm' | 'marea' | 'boss' | 'unsleeping' | 'dragon' | 'deep' | 'night' | 'silent';

/** A battle's own music: the dragon's clearing (saga 7), underground (but the Furrows, under the sky) or a side saga's night; the Marea's otherwise. */
export const battleMood = (b: BattleState): Mood => (b.rules?.dragon ? 'dragon' : b.rules?.night ? 'night' : b.mode === 'dungeon' && !b.dungeon?.maze ? 'deep' : 'marea');

interface Voice {
  out: GainNode;
  stop(): void;
  /** File tracks play through an <audio> element, which keeps going when the tab hides unless paused. */
  el?: HTMLAudioElement;
}

/** Moods with more than one take (`{mood}.mp3`, `{mood}-2.mp3`…): each time it starts, one of them at random. */
const TAKES: Partial<Record<Mood, number>> = { deep: 3, night: 2 };

const NOTE = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);

// D dorian for calm, D aeolian for the Marea and the night, D phrygian-ish for the boss and the deep.
const SCALES: Record<Exclude<Mood, 'silent'>, number[]> = {
  calm: [0, 2, 3, 5, 7, 9, 10],
  marea: [0, 2, 3, 5, 7, 8, 10],
  boss: [0, 1, 3, 5, 6, 8, 10],
  unsleeping: [0, 1, 3, 5, 7, 8, 10],
  dragon: [0, 1, 3, 5, 6, 8, 10],
  deep: [0, 1, 3, 5, 7, 8, 10],
  night: [0, 2, 3, 5, 7, 8, 10],
};
const PROGRESSIONS: Record<Exclude<Mood, 'silent'>, number[]> = {
  calm: [0, 5, 3, 4],
  marea: [0, 5, 6, 4],
  boss: [0, 1, 0, 6],
  unsleeping: [0, 5, 1, 0],
  dragon: [0, 1, 6, 5],
  deep: [0, 6, 5, 1],
  night: [0, 3, 6, 4],
};
const TEMPO: Record<Exclude<Mood, 'silent'>, number> = { calm: 66, marea: 96, boss: 118, unsleeping: 110, dragon: 120, deep: 90, night: 80 };

class Music {
  private mood: Mood = 'silent';
  private voice: Voice | null = null;
  private files = new Map<string, boolean>();

  private wanted: Mood = 'silent';
  /** The bastion's era: from the III on, the calm is the grown fortress's own (`calm-era<n>.mp3`, or the nearest era below that has one). */
  private era = 1;

  /** The group's era changed (the bastion opened, or a Gran Obra rose): the calm follows it, crossfading if it is playing. */
  setEra(era: number): void {
    if (era === this.era) return;
    this.era = era;
    if (this.mood === 'calm') {
      this.mood = 'silent';
      void this.play('calm');
    }
  }

  /** The page went to the background (tab switched, phone locked, app closed): silence everything. */
  hide(): void {
    this.voice?.el?.pause();
    void audio.ctx?.suspend().catch(() => undefined);
  }

  /** Back in front: pick the music up where it was. */
  show(): void {
    if (!audio.ctx) return;
    void audio.ctx.resume().then(() => {
      void this.voice?.el?.play().catch(() => undefined);
      this.resume();
    }).catch(() => undefined);
  }

  /** Call after the audio context unlocks: starts whatever mood was requested before. */
  resume(): void {
    if (this.mood !== this.wanted) void this.play(this.wanted);
  }

  /** Crossfades to a mood (no-op if already there). */
  async play(mood: Mood): Promise<void> {
    this.wanted = mood;
    const ctx = audio.ctx;
    const dest = audio.musicGain;
    if (!ctx || !dest || ctx.state !== 'running') return;
    if (mood === this.mood) return;
    this.mood = mood;
    (window as unknown as { __sfxLog?: { name: string; v: number; at: number }[] }).__sfxLog?.push({ name: `music:${mood}`, v: 1, at: performance.now() });
    const old = this.voice;
    this.voice = null;
    if (old) {
      old.out.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
      window.setTimeout(() => old.stop(), 3000);
    }
    if (mood === 'silent') return;
    const season = seasonNow(Date.now());
    const take = Math.floor(Math.random() * (TAKES[mood] ?? 1));
    let v = season ? await this.fromFile(ctx, dest, `${mood}-${season}`) : null;
    for (let e = this.era; !v && mood === 'calm' && e >= 3; e--) v = await this.fromFile(ctx, dest, `calm-era${e}`);
    v ??=
      (take ? await this.fromFile(ctx, dest, `${mood}-${take + 1}`) : null) ??
      (await this.fromFile(ctx, dest, mood)) ??
      this.generative(ctx, dest, mood);
    if (this.mood !== mood) {
      v.stop();
      return;
    }
    this.voice = v;
  }

  private async fromFile(ctx: AudioContext, dest: AudioNode, mood: string): Promise<Voice | null> {
    const url = `${import.meta.env.BASE_URL}audio/${mood}.mp3`;
    if (this.files.get(url) === false) return null;
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (!res.ok || !(res.headers.get('content-type') ?? '').includes('audio')) {
        this.files.set(url, false);
        return null;
      }
    } catch {
      this.files.set(url, false);
      return null;
    }
    const el = new Audio(url);
    el.loop = true;
    el.crossOrigin = 'anonymous';
    const src = ctx.createMediaElementSource(el);
    const out = ctx.createGain();
    out.gain.value = 0;
    src.connect(out).connect(dest);
    out.gain.setTargetAtTime(1, ctx.currentTime, 1.2);
    void el.play().catch(() => undefined);
    return {
      out,
      el,
      stop: () => {
        el.pause();
        out.disconnect();
      },
    };
  }

  private generative(ctx: AudioContext, dest: AudioNode, mood: Exclude<Mood, 'silent'>): Voice {
    const out = ctx.createGain();
    out.gain.value = 0;
    out.gain.setTargetAtTime(1, ctx.currentTime, 1.5);
    // A long, dark room.
    const verb = ctx.createConvolver();
    verb.buffer = impulse(ctx, mood === 'calm' ? 3.6 : 2.4);
    const wet = ctx.createGain();
    wet.gain.value = mood === 'calm' ? 0.45 : 0.3;
    const dry = ctx.createGain();
    dry.gain.value = 0.8;
    const bus = ctx.createGain();
    bus.connect(dry).connect(out);
    bus.connect(verb).connect(wet).connect(out);
    out.connect(dest);
    const scale = SCALES[mood];
    const prog = PROGRESSIONS[mood];
    const beat = 60 / TEMPO[mood];
    const root = 50; // D3
    let rng = 0x2f6b1;
    const rand = (): number => {
      rng = (Math.imul(rng, 1103515245) + 12345) >>> 0;
      return rng / 4294967296;
    };
    const deg = (d: number, oct = 0): number => {
      const n = scale.length;
      const o = Math.floor(d / n);
      return root + oct * 12 + o * 12 + (scale[((d % n) + n) % n] as number);
    };
    let bar = 0;
    let next = ctx.currentTime + 0.1;
    const stopped = { v: false };

    const pluck = (t: number, midi: number, vel: number, bright = 2400): void => {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = NOTE(midi);
      const o2 = ctx.createOscillator();
      o2.type = 'square';
      o2.frequency.value = NOTE(midi) * 2.001;
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.setValueAtTime(bright, t);
      f.frequency.exponentialRampToValueAtTime(300, t + 0.9);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.16 * vel, t + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
      const g2 = ctx.createGain();
      g2.gain.value = 0.18;
      o.connect(f);
      o2.connect(g2).connect(f);
      f.connect(g).connect(bus);
      o.start(t);
      o2.start(t);
      o.stop(t + 1.7);
      o2.stop(t + 1.7);
    };

    const pad = (t: number, midis: number[], dur: number, level: number, type: OscillatorType, vowel: number): void => {
      for (const m of midis) {
        for (const det of [-6, 5]) {
          const o = ctx.createOscillator();
          o.type = type;
          o.frequency.value = NOTE(m);
          o.detune.value = det;
          const f = ctx.createBiquadFilter();
          f.type = 'bandpass';
          f.frequency.value = vowel;
          f.Q.value = 1.4;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(level, t + dur * 0.35);
          g.gain.exponentialRampToValueAtTime(0.0001, t + dur * 1.05);
          o.connect(f).connect(g).connect(bus);
          o.start(t);
          o.stop(t + dur * 1.1);
        }
      }
    };

    const drum = (t: number, freq: number, vel: number, len = 0.5): void => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq * 2.2, t);
      o.frequency.exponentialRampToValueAtTime(freq, t + 0.08);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5 * vel, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, t + len);
      o.connect(g).connect(bus);
      o.start(t);
      o.stop(t + len + 0.05);
      // Skin noise.
      const n = ctx.createBufferSource();
      n.buffer = noiseBuffer(ctx);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 900;
      const gn = ctx.createGain();
      gn.gain.setValueAtTime(0.12 * vel, t);
      gn.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      n.connect(f).connect(gn).connect(bus);
      n.start(t, rand() * 0.5);
      n.stop(t + 0.15);
    };

    const scheduleBar = (t: number): void => {
      const chord = prog[bar % prog.length] as number;
      const triad = [deg(chord), deg(chord + 2), deg(chord + 4)];
      const barLen = beat * 4;
      if (mood === 'calm') {
        // Low strings and a soft choir under a lute arpeggio; a harp run every 4 bars.
        pad(t, [triad[0]! - 12], barLen, 0.05, 'sawtooth', 500);
        if (bar % 2 === 0) pad(t, [triad[1]! + 12, triad[2]! + 12], barLen * 2, 0.018, 'sine', 800);
        const pattern = [0, 1, 2, 1, 3, 1, 2, 1];
        pattern.forEach((p, i) => {
          if (rand() < 0.12) return;
          const note = p === 3 ? triad[0]! + 12 : (triad[p] as number) + 12;
          pluck(t + i * (beat / 2), note, 0.8 + rand() * 0.3);
        });
        if (bar % 4 === 3) for (let i = 0; i < 7; i++) pluck(t + barLen - beat + i * 0.07, deg(chord + i, 2), 0.35, 4000);
      } else if (mood !== 'boss' && mood !== 'unsleeping' && mood !== 'dragon') {
        // The Marea's pattern, also under deep and night (their own scale and tempo) when their file is missing.
        pad(t, [triad[0]! - 12, triad[0]! - 24], barLen, 0.06, 'sawtooth', 380);
        for (let i = 0; i < 8; i++) {
          const n = i % 4 === 3 ? triad[1]! : triad[0]!;
          pluck(t + i * (beat / 2), n, 0.7, 1200);
        }
        [0, 1.5, 2, 3, 3.5].forEach((b, i) => drum(t + b * beat, i === 0 || i === 2 ? 55 : 82, i === 0 ? 1 : 0.6));
        if (bar % 4 === 0) pad(t, [triad[0]! + 12, triad[2]! + 12], beat * 2, 0.03, 'sawtooth', 1400);
      } else {
        pad(t, [triad[0]! - 12, triad[0]! - 11], barLen, 0.06, 'sawtooth', 300);
        for (let i = 0; i < 8; i++) drum(t + i * (beat / 2), i % 2 ? 70 : 50, i % 4 === 0 ? 1 : 0.55, 0.35);
        if (bar % 2 === 0) pad(t + beat * 2, triad.map((x) => x + 12), beat * 1.5, 0.04, 'sawtooth', 900);
        for (let i = 0; i < 4; i++) pluck(t + i * beat + beat / 2, triad[(i + bar) % 3]! + 12, 0.6, 1800);
      }
      bar++;
    };

    const tick = (): void => {
      if (stopped.v) return;
      while (next < ctx.currentTime + 0.6) {
        scheduleBar(next);
        next += beat * 4;
      }
    };
    tick();
    const timer = window.setInterval(tick, 200);
    return {
      out,
      stop: () => {
        stopped.v = true;
        window.clearInterval(timer);
        window.setTimeout(() => out.disconnect(), 4000);
      },
    };
  }
}

let NOISE: AudioBuffer | null = null;
const noiseBuffer = (ctx: AudioContext): AudioBuffer => {
  if (NOISE) return NOISE;
  NOISE = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = NOISE.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return NOISE;
};

const impulse = (ctx: AudioContext, seconds: number): AudioBuffer => {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2.6;
  }
  return buf;
};

export const music = new Music();

// Phones keep a page's audio running in the background: stop it whenever the page is not visible.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => (document.visibilityState === 'hidden' ? music.hide() : music.show()));
  window.addEventListener('pagehide', () => music.hide());
}
